/**
 * Pure helpers for Explore parameter validation and state.
 *
 * These rules are shared between:
 * - The first-load card initiation dialog (ExploreCardInitiatorDialog)
 * - The in-chat follow-up flow forms (FlowActionMonthYearForm, FlowActionYearRangeForm)
 */

import type { ExploreRequiredCondition } from '../types/explore.types'
import type { ActionSelections } from '../types/chat.types'

/** Valid year range accepted by both the frontend and backend. */
export const YEAR_MIN = 2000
export const YEAR_MAX = 2100

export function isValidYear(value: number | null | undefined): value is number {
  return typeof value === 'number' && value >= YEAR_MIN && value <= YEAR_MAX
}

export function isValidMonth(value: number | null | undefined): value is number {
  return typeof value === 'number' && value >= 1 && value <= 12
}

/**
 * Validate an `ActionSelections` object against a required condition.
 * Returns true when the selections satisfy the condition's requirements.
 */
export function areSelectionsValid(
  condition: ExploreRequiredCondition,
  selections: Partial<ActionSelections>,
): boolean {
  if (condition === 'month_and_year') {
    // Month is optional; year is required
    const monthOk =
      selections.monthChosen == null || isValidMonth(selections.monthChosen)
    const yearOk = isValidYear(selections.yearChosen)
    return monthOk && yearOk
  }

  if (condition === 'year1_and_year2') {
    return isValidYear(selections.year1) && isValidYear(selections.year2)
  }

  return false
}
